import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Mail, Phone, Instagram, Linkedin, ArrowUpRight, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { trackEvent } from '../lib/posthog';
import WhatsAppIcon from './icons/WhatsAppIcon';
import Logo from './Logo';

/**
 * NavyFooter v2 (R3.6, CLAUDE.md + design-reference/startseite.html):
 * die einzige Navy-Fläche der Seite — oberer Block ist der integrierte
 * Schluss-CTA (Display-Headline + Primary „Zimmer ansehen" + WhatsApp-
 * Outline), darunter kompakte Link-Spalten und die Trust-Zeile
 * (Logo invertiert + GmbH · UID · Gerlafingen SO).
 */
const Footer = () => {
  const { t, language } = useLanguage();
  const location = useLocation();

  const quickLinks = [
    { name: t.nav.home, href: '/' },
    { name: t.nav.apartments, href: '/wohnungen' },
    { name: t.nav.rooms, href: '/zimmer' },
    { name: t.nav.blog, href: '/blog' },
    { name: t.nav.about, href: '/about' },
    { name: t.nav.contact, href: '/contact' },
  ];

  const services = [
    { name: t.services.service1Title, href: '/wohnungen' },
    { name: t.services.service2Title, href: '/zimmer' },
    { name: t.nav.forCompanies, href: '/for-companies' },
    { name: t.nav.forPropertyManagers, href: '/for-property-managers' },
  ];

  // Stadtname führt auf die Zimmer-Route (Geldseiten, R1.6/H7),
  // daneben ein kompakter Sekundärlink auf die Wohnungs-Route.
  const locations = [
    { name: 'Zürich', slug: 'zuerich' },
    { name: 'Basel', slug: 'basel' },
    { name: 'Bern', slug: 'bern' },
    { name: 'Genf', slug: 'genf' },
  ];

  return (
    <footer className="bg-ink text-white" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Schluss-CTA (integriert — ersetzt separate CTA-Sektionen der Seiten) */}
        <div className="py-14 lg:py-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border-b border-white/10">
          <div className="max-w-xl">
            <h2 className="font-display text-h2 font-bold text-white mb-3">
              {t.footer.ctaTitle}
            </h2>
            <p className="text-base text-white/70">{t.footer.ctaText}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button asChild data-testid="footer-cta-rooms">
              <Link to="/zimmer">
                {t.footer.ctaButton}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
              data-testid="footer-cta-whatsapp"
            >
              <a
                href={buildWhatsAppUrl({ source: 'footer', url: location.pathname })}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { source: 'footer' })}
              >
                {/* WhatsApp-Grün auf Navy: #4FCB7E (CLAUDE.md) */}
                <WhatsAppIcon className="h-4 w-4 text-[#4FCB7E]" />
                {t.footer.ctaWhatsApp}
              </a>
            </Button>
          </div>
        </div>

        {/* Link-Spalten (kompakt, Kontraste >= white/70) */}
        <div className="py-12 lg:py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-4 space-y-4">
            <Logo variant="dark" />
            <p className="text-sm text-white/70 leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
            <p className="text-sm text-white/70">{t.footer.headquarters}</p>

            {/* Social Links */}
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://www.instagram.com/feelathomenow"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-brand rounded-[10px] flex items-center justify-center transition-colors group"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4 text-white/70 group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://www.linkedin.com/in/feelathomenow-656897389"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-brand rounded-[10px] flex items-center justify-center transition-colors group"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4 text-white/70 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <nav className="lg:col-span-2" aria-label={t.footer.quickLinks}>
            <h3 className="text-xs font-semibold text-white/70 uppercase tracking-widest mb-5">
              {t.footer.quickLinks}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-white/70 hover:text-white transition-colors flex items-center group"
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Services */}
          <nav className="lg:col-span-3" aria-label={t.footer.services}>
            <h3 className="text-xs font-semibold text-white/70 uppercase tracking-widest mb-5">
              {t.footer.services}
            </h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-sm text-white/70 hover:text-white transition-colors flex items-center group"
                  >
                    {service.name}
                    <ArrowUpRight className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Locations & Contact */}
          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold text-white/70 uppercase tracking-widest mb-5">
              {t.footer.activeIn}
            </h3>
            <ul className="space-y-3 mb-8">
              {locations.map((loc) => (
                <li key={loc.name} className="flex items-center text-sm">
                  <MapPin className="h-3 w-3 mr-2 text-white/45 shrink-0" aria-hidden="true" />
                  <Link
                    to={`/zimmer/${loc.slug}`}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {loc.name}
                  </Link>
                  <span className="text-white/45 mx-2" aria-hidden="true">·</span>
                  <Link
                    to={`/wohnungen/${loc.slug}`}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {t.nav.apartments}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="space-y-3">
              <a
                href="mailto:info@feelathomenow.ch"
                className="text-sm text-white/70 hover:text-white transition-colors flex items-center"
              >
                <Mail className="h-3.5 w-3.5 mr-2 text-white/45" aria-hidden="true" />
                info@feelathomenow.ch
              </a>
              <a
                href="tel:+41762398070"
                className="text-sm text-white/70 hover:text-white transition-colors flex items-center"
              >
                <Phone className="h-3.5 w-3.5 mr-2 text-white/45" aria-hidden="true" />
                +41 76 239 80 70
              </a>
            </div>
          </div>
        </div>

        {/* Trust-Zeile: Logo invertiert + GmbH · UID · Sitz (W20/H5), DE/EN identisch */}
        <div className="py-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-6">
          <div className="flex items-center gap-2.5">
            <Logo variant="inverted" size={20} withWordmark={false} />
            <p className="text-xs text-white/70">
              FeelAtHomeNow GmbH · CHE-190.416.711 · Gerlafingen SO
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to={language === 'en' ? '/legal' : '/impressum'}
              className="text-xs text-white/70 hover:text-white transition-colors"
            >
              {language === 'en' ? 'Legal Notice' : 'Impressum'}
            </Link>
            <Link
              to={language === 'en' ? '/privacy' : '/datenschutz'}
              className="text-xs text-white/70 hover:text-white transition-colors"
            >
              {language === 'en' ? 'Privacy Policy' : 'Datenschutz'}
            </Link>
            <a
              href="mailto:info@feelathomenow.ch"
              className="text-xs text-white/70 hover:text-white transition-colors"
            >
              info@feelathomenow.ch
            </a>
            <span className="text-xs text-white/45">&copy; {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
